import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Mail, RefreshCw, Loader2, ArrowDownLeft, ArrowUpRight, Paperclip, ExternalLink, Sparkles, Send, UserCheck, UserPlus,
  FileSignature, CalendarClock, Briefcase, MessagesSquare, Search, ChevronDown, ChevronRight, Landmark, ShieldAlert,
} from 'lucide-react';

const CATS = {
  nda: { label: 'NDA', Icon: FileSignature, cls: 'text-rose-300 border-rose-500/40 bg-rose-500/10' },
  termin: { label: 'Termin', Icon: CalendarClock, cls: 'text-amber-300 border-amber-500/40 bg-amber-500/10' },
  projekt: { label: 'Projekt', Icon: Briefcase, cls: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10' },
  austausch: { label: 'Austausch', Icon: MessagesSquare, cls: 'text-slate-300 border-slate-500/40 bg-slate-500/10' },
};

const ago = (iso) => {
  if (!iso) return '';
  const min = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 60) return `vor ${min} Min`;
  if (min < 60 * 24) return `vor ${Math.floor(min / 60)} Std`;
  const d = Math.floor(min / (60 * 24));
  return d === 1 ? 'gestern' : `vor ${d} Tagen`;
};

const shortMailbox = (acc) => (acc || '').split('@')[0];

function MailBodyDialog({ mailId, open, onOpenChange }) {
  const [body, setBody] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open || !mailId) return;
    setBody(null); setError('');
    api.getBrokerMailBody(mailId)
      .then(r => setBody(r.data))
      .catch(err => setError(err.response?.data?.detail || 'E-Mail konnte nicht geladen werden'));
  }, [open, mailId]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[85vh] flex flex-col" data-testid="broker-mail-body-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base text-foreground pr-6 truncate">{body?.subject || 'E-Mail'}</DialogTitle>
        </DialogHeader>
        {error ? (
          <p className="text-sm text-red-400 py-4" data-testid="broker-mail-body-error">{error}</p>
        ) : !body ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col gap-2">
            <div className="text-[11px] text-muted-foreground space-y-0.5 border-b border-border pb-2">
              <p><span className="text-foreground/40">Von:</span> {body.from?.name || ''} &lt;{body.from?.address || ''}&gt;</p>
              <p className="truncate"><span className="text-foreground/40">An:</span> {(body.to || []).map(t => t.address).join(', ')}</p>
              <p><span className="text-foreground/40">Postfach:</span> {body.mailbox_account}
                {body.direction === 'outbound' && body.from?.address && body.mailbox_account
                  && body.from.address.toLowerCase() !== body.mailbox_account.toLowerCase()
                  && <span className="text-amber-400/80"> (Kopie — gesendet von {body.from.address})</span>}</p>
            </div>
            {body.content_type === 'html' ? (
              <iframe sandbox="" srcDoc={body.body} title="broker-mail-body"
                className="flex-1 w-full min-h-[45vh] bg-white rounded-sm border border-border" data-testid="broker-mail-body-frame" />
            ) : (
              <pre className="flex-1 overflow-y-auto text-xs text-foreground whitespace-pre-wrap p-3 bg-secondary/30 rounded-sm min-h-[30vh]">{body.body}</pre>
            )}
            {body.web_link && (
              <a href={body.web_link} target="_blank" rel="noreferrer"
                className="text-[11px] text-blue-400 hover:text-blue-300 flex items-center gap-1 w-fit" data-testid="broker-mail-outlook-link">
                <ExternalLink className="w-3 h-3" /> In Outlook öffnen
              </a>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReplyDialog({ thread, open, onOpenChange, onSent }) {
  const [draft, setDraft] = useState(null);
  const [subject, setSubject] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!open || !thread) return;
    setDraft(null); setText(''); setSubject('');
    api.draftBrokerThreadReply(thread.id)
      .then(r => { setDraft(r.data); setSubject(r.data.subject); setText(r.data.body); })
      .catch(err => { toast.error(err.response?.data?.detail || 'KI-Entwurf fehlgeschlagen'); setDraft({ failed: true, subject: `AW: ${thread.subject}`, body: '', to: thread.broker_email }); setSubject(`AW: ${thread.subject}`); });
  }, [open, thread]);
  const send = async () => {
    setSending(true);
    try {
      const r = await api.sendBrokerThreadReply(thread.id, { subject, body: text });
      toast.success(r.data.sent_via === 'own_thread'
        ? 'Antwort im Thread aus deinem Postfach gesendet'
        : 'Antwort aus deinem Postfach gesendet (letzte Broker-Mail zitiert)');
      onOpenChange(false);
      onSent?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Senden fehlgeschlagen');
    }
    setSending(false);
  };
  if (!thread) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="broker-reply-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base text-foreground flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-sky-400" /> Antwort an {thread.broker_name}
          </DialogTitle>
        </DialogHeader>
        {!draft ? (
          <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground text-xs" data-testid="broker-reply-drafting">
            <Loader2 className="w-4 h-4 animate-spin" /> KI schreibt einen Entwurf…
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground">An</label>
              <p className="text-xs text-foreground mt-0.5">{thread.broker_email}</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Betreff</label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} data-testid="broker-reply-subject"
                className="bg-secondary border-border rounded-sm text-xs h-8 mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Nachricht (KI-Entwurf — anpassbar)</label>
              <textarea value={text} onChange={e => setText(e.target.value)} rows={10} data-testid="broker-reply-body"
                className="w-full mt-1 bg-secondary border border-border rounded-sm text-xs text-foreground p-2.5 leading-relaxed focus:outline-none focus:border-sky-500/50" />
              <p className="text-[10px] text-muted-foreground mt-1">Die OLTA-Signatur mit deinem Namen wird beim Senden automatisch angehängt.</p>
            </div>
            <p className="text-[11px] text-muted-foreground border border-border/60 bg-secondary/30 rounded-sm px-2.5 py-1.5" data-testid="broker-reply-send-note">
              Wird aus <span className="text-foreground/80">deinem Postfach</span> als Antwort im Thread gesendet — mit vollständigem E-Mail-Verlauf. Antworten gehen nie über ein anderes Team-Postfach raus.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm text-xs h-8 border-border">Abbrechen</Button>
              <Button size="sm" onClick={send} disabled={sending || !text.trim()} data-testid="broker-reply-send-btn"
                className="bg-sky-600 hover:bg-sky-500 text-white rounded-sm text-xs h-8">
                {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Senden
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ThreadMessages({ threadId, onOpenMail }) {
  const [msgs, setMsgs] = useState(null);
  useEffect(() => {
    api.getBrokerThreadMessages(threadId).then(r => setMsgs(r.data.messages)).catch(() => setMsgs([]));
  }, [threadId]);
  if (msgs === null) return <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>;
  return (
    <div className="border-t border-border/60 mt-2 pt-2 space-y-1" data-testid={`thread-messages-${threadId}`}>
      {msgs.map(m => (
        <button key={m.id} onClick={() => onOpenMail(m.id)} data-testid={`thread-msg-${m.id}`}
          className="w-full text-left flex items-start gap-2.5 rounded-sm px-2 py-1.5 hover:bg-secondary/40 transition-colors">
          {m.direction === 'inbound'
            ? <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
            : <ArrowUpRight className="w-3.5 h-3.5 text-sky-400 mt-0.5 shrink-0" />}
          <span className="flex-1 min-w-0">
            <span className="text-[11px] font-medium text-foreground/90">
              {m.direction === 'inbound' ? (m.from_name || m.from_email) : `OLTA · ${m.from_name || shortMailbox(m.mailbox_account)}`}
            </span>
            <span className="text-[10px] text-muted-foreground ml-2">{ago(m.ts)} · {shortMailbox(m.direction === 'inbound' ? m.mailbox_account : (m.from_email || m.mailbox_account))}</span>
            {m.has_attachment && <Paperclip className="w-3 h-3 inline ml-1.5 text-amber-400" />}
            <span className="block text-[11px] text-muted-foreground truncate">{m.snippet}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

export default function BrokerTrafficPage() {
  const { user } = useAuth();
  const canAddBroker = (user?.email || '').toLowerCase() === 'talu@olta-nachfolge.com';
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const [mailbox, setMailbox] = useState('all');
  const [ballBeiUns, setBallBeiUns] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [mailId, setMailId] = useState(null);
  const [replyThread, setReplyThread] = useState(null);
  const autoSynced = useRef(false);

  const load = useCallback(() => api.getBrokerTraffic().then(r => { setData(r.data); return r.data; }).catch(() => null), []);

  const sync = useCallback(async (silent = false) => {
    setSyncing(true);
    try {
      const r = await api.syncBrokerTraffic();
      await load();
      if (!silent) toast.success(r.data.new > 0 ? `${r.data.new} neue Mail(s) gefunden` : 'Alles aktuell');
    } catch {
      if (!silent) toast.error('Sync fehlgeschlagen');
    }
    setSyncing(false);
  }, [load]);

  useEffect(() => {
    load().then(d => {
      if (autoSynced.current) return;
      autoSynced.current = true;
      const stale = !d?.last_synced_at || (Date.now() - new Date(d.last_synced_at).getTime()) > 10 * 60 * 1000;
      if (stale) sync(true);
    });
  }, [load, sync]);

  const threads = (data?.threads || []).filter(t => {
    if (cat !== 'all' && (t.category || 'neu') !== cat) return false;
    if (mailbox !== 'all' && !(t.participants || []).includes(mailbox)) return false;
    if (ballBeiUns && !t.needs_reply) return false;
    if (q) {
      const hay = `${t.broker_name} ${t.company} ${t.broker_email} ${t.subject} ${t.ai_summary} ${t.linked_project_name}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const catChips = [
    { key: 'all', label: `Alle (${(data?.threads || []).length})` },
    ...Object.entries(CATS).map(([k, v]) => ({ key: k, label: `${v.label} (${data?.counts?.[k] || 0})`, Icon: v.Icon })),
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4" data-testid="broker-traffic-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl text-foreground flex items-center gap-2.5">
            <Mail className="w-6 h-6 text-sky-400" /> Broker-Mailverkehr
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Alle E-Mail-Threads mit Brokern über sämtliche Team-Postfächer — chronologisch nach letzter Aktivität,
            KI-eingeordnet nach dem, worum es gerade geht.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.last_synced_at && (
            <span className="text-[10px] text-muted-foreground" data-testid="broker-traffic-last-synced">
              Sync: {ago(data.last_synced_at)}
            </span>
          )}
          <Button size="sm" onClick={() => sync(false)} disabled={syncing} data-testid="broker-traffic-sync-btn"
            className="bg-sky-600 hover:bg-sky-500 text-white rounded-sm text-xs h-8">
            {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            {syncing ? 'Synchronisiere…' : 'Aktualisieren'}
          </Button>
        </div>
      </div>

      {(data?.no_mail_access || []).length > 0 && (
        <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm px-3.5 py-2.5 flex items-start gap-2.5 flex-wrap"
          data-testid="broker-traffic-no-access-hint">
          <ShieldAlert className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-[260px]">
            <p className="text-xs text-amber-200 font-medium">
              Kein E-Mail-Zugriff auf: {data.no_mail_access.map(shortMailbox).join(', ')}
            </p>
            <p className="text-[11px] text-amber-200/70 mt-0.5">
              Diese Postfächer fehlen in der Übersicht. Der jeweilige Besitzer muss sich einloggen und einmal den
              E-Mail-Zugriff erlauben — der Button führt durch die Microsoft-Freigabe (für dein eigenes Konto).
            </p>
          </div>
          <Button size="sm" variant="outline" data-testid="broker-traffic-grant-access-btn"
            onClick={async () => {
              try { const r = await api.mstodoConnect(); window.location.href = r.data.auth_url; }
              catch { toast.error('Verbindung konnte nicht gestartet werden'); }
            }}
            className="rounded-sm text-xs h-8 border-amber-500/40 text-amber-200 hover:bg-amber-500/20">
            Mail-Zugriff für mein Konto erlauben
          </Button>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Broker, Firma, Betreff…"
            data-testid="broker-traffic-search" className="bg-secondary border-border rounded-sm text-xs h-8 pl-8 w-56" />
        </div>
        {catChips.map(c => (
          <button key={c.key} onClick={() => setCat(c.key)} data-testid={`broker-traffic-cat-${c.key}`}
            className={`text-[11px] font-mono border rounded-sm px-2.5 py-1.5 flex items-center gap-1.5 transition-colors ${
              cat === c.key ? 'border-sky-500/60 bg-sky-500/15 text-sky-300' : 'border-border bg-secondary/40 text-foreground/70 hover:bg-secondary'}`}>
            {c.Icon && <c.Icon className="w-3 h-3" />}{c.label}
          </button>
        ))}
        <button onClick={() => setBallBeiUns(v => !v)} data-testid="broker-traffic-needs-reply-toggle"
          className={`text-[11px] font-mono border rounded-sm px-2.5 py-1.5 flex items-center gap-1.5 transition-colors ${
            ballBeiUns ? 'border-orange-500/60 bg-orange-500/15 text-orange-300' : 'border-border bg-secondary/40 text-foreground/70 hover:bg-secondary'}`}>
          <ArrowDownLeft className="w-3 h-3" /> Ball bei uns
        </button>
        {(data?.mailboxes || []).length > 1 && (
          <select value={mailbox} onChange={e => setMailbox(e.target.value)} data-testid="broker-traffic-mailbox-filter"
            className="text-[11px] font-mono bg-secondary border border-border rounded-sm px-2 py-1.5 text-foreground/80">
            <option value="all">Alle Postfächer</option>
            {data.mailboxes.map(m => <option key={m} value={m}>{shortMailbox(m)}</option>)}
          </select>
        )}
      </div>

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : threads.length === 0 ? (
        <div className="border border-border rounded-sm bg-card p-8 text-center" data-testid="broker-traffic-empty">
          <p className="text-sm text-muted-foreground">
            {(data.threads || []).length === 0
              ? 'Noch keine Broker-Mails gefunden — „Aktualisieren" lädt die letzten 30 Tage aus allen verbundenen Postfächern.'
              : 'Keine Threads für diesen Filter.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {threads.map(t => {
            const c = CATS[t.category] || null;
            const isOpen = expanded === t.id;
            return (
              <div key={t.id} className={`border rounded-sm bg-card transition-colors ${t.needs_reply ? 'border-orange-500/30' : 'border-border'}`}
                data-testid={`broker-thread-${t.id}`}>
                <div role="button" tabIndex={0} onClick={() => setExpanded(isOpen ? null : t.id)}
                  onKeyDown={e => e.key === 'Enter' && setExpanded(isOpen ? null : t.id)}
                  className="w-full text-left px-3.5 py-3 cursor-pointer"
                  data-testid={`broker-thread-toggle-${t.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                    <span className="text-sm font-medium text-foreground">{t.broker_name}</span>
                    {t.company && <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Landmark className="w-3 h-3" />{t.company}</span>}
                    {t.contact_id ? (
                      <Link to={`/contacts?q=${encodeURIComponent(t.broker_email)}`} onClick={e => e.stopPropagation()}
                        data-testid={`thread-crm-badge-${t.id}`}
                        className="text-[10px] font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 rounded px-1.5 py-0.5 flex items-center gap-1 hover:bg-emerald-500/20">
                        <UserCheck className="w-3 h-3" /> Im CRM
                      </Link>
                    ) : canAddBroker ? (
                      <button onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const r = await api.addBrokerThreadContact(t.id);
                            toast.success(r.data.created ? `${t.broker_name} als Broker im CRM angelegt` : 'Bestehender CRM-Kontakt verknüpft');
                            load();
                          } catch (err) { toast.error(err.response?.data?.detail || 'Aufnahme fehlgeschlagen'); }
                        }}
                        data-testid={`thread-add-contact-btn-${t.id}`}
                        className="text-[10px] font-mono border border-sky-500/40 bg-sky-500/10 text-sky-300 rounded px-1.5 py-0.5 flex items-center gap-1 hover:bg-sky-500/25 transition-colors">
                        <UserPlus className="w-3 h-3" /> Als Broker aufnehmen
                      </button>
                    ) : null}
                    {c && (
                      <span className={`text-[10px] font-mono border rounded px-1.5 py-0.5 flex items-center gap-1 ${c.cls}`} data-testid={`thread-cat-badge-${t.id}`}>
                        <c.Icon className="w-3 h-3" /> {c.label}
                      </span>
                    )}
                    {t.needs_reply && (
                      <span className="text-[10px] font-mono border border-orange-500/40 bg-orange-500/10 text-orange-300 rounded px-1.5 py-0.5" data-testid={`thread-needs-reply-${t.id}`}>
                        Ball bei uns
                      </span>
                    )}
                    {t.linked_project_id && (
                      <Link to={`/projects/${t.linked_project_id}`} onClick={e => e.stopPropagation()}
                        data-testid={`thread-deal-link-${t.id}`}
                        className="text-[10px] font-mono border border-violet-500/40 bg-violet-500/10 text-violet-300 rounded px-1.5 py-0.5 hover:bg-violet-500/20">
                        {t.linked_project_name || 'Deal'} →
                      </Link>
                    )}
                    <span className="ml-auto text-[10px] text-muted-foreground shrink-0">
                      {t.msg_count} Mail{t.msg_count !== 1 ? 's' : ''} · <span title="Team-Postfach, in dem dieser Thread liegt (auch als Empfänger/CC) — nicht zwingend der Absender">{(t.participants || []).map(shortMailbox).join(', ')}</span> · {ago(t.last_ts)}
                    </span>
                  </div>
                  <p className="text-xs text-foreground/80 mt-1.5 truncate pl-5.5" style={{ paddingLeft: '22px' }}>{t.subject}</p>
                  {t.ai_summary && (
                    <p className="text-[11px] text-sky-300/90 italic mt-0.5 truncate" style={{ paddingLeft: '22px' }} data-testid={`thread-ai-summary-${t.id}`}>
                      {t.ai_summary}
                    </p>
                  )}
                  <p className="text-[11px] text-muted-foreground mt-0.5 truncate" style={{ paddingLeft: '22px' }}>
                    <span className={t.last_direction === 'inbound' ? 'text-emerald-400' : 'text-sky-400'}>
                      {t.last_direction === 'inbound' ? '↙ ' : '↗ '}
                      {t.last_direction === 'inbound' ? (t.last_from_name || 'Broker') : `OLTA · ${t.last_from_name || shortMailbox(t.last_mailbox)}`}:
                    </span>{' '}{t.last_snippet}
                  </p>
                </div>
                {isOpen && (
                  <div className="px-3.5 pb-3">
                    <ThreadMessages threadId={t.id} onOpenMail={setMailId} />
                    <Button size="sm" onClick={() => setReplyThread(t)} data-testid={`broker-thread-reply-btn-${t.id}`}
                      className="mt-2 bg-sky-600 hover:bg-sky-500 text-white rounded-sm text-xs h-7 px-2.5">
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Antworten (KI-Entwurf)
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <MailBodyDialog mailId={mailId} open={!!mailId} onOpenChange={v => !v && setMailId(null)} />
      <ReplyDialog thread={replyThread} open={!!replyThread} onOpenChange={v => !v && setReplyThread(null)} onSent={load} />
    </div>
  );
}
